/**
 * Input validation utility for preventing SSRF and injection attacks
 */
export class InputValidationUtil {
  
  // Allowed characters for client codes (alphanumeric, hyphens, underscores)
  private static readonly CLIENT_CODE_PATTERN = /^[a-zA-Z0-9_-]+$/;
  
  // Allowed characters for currency codes (3-letter ISO codes)
  private static readonly CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/;
  
  // Maximum lengths to prevent buffer overflow attacks
  private static readonly MAX_CLIENT_CODE_LENGTH = 50;
  private static readonly MAX_CURRENCY_CODE_LENGTH = 3;
  
  // Common SSRF attack patterns to block
  private static readonly SSRF_PATTERNS = [
    /localhost/i,
    /127\.0\.0\.1/,
    /0\.0\.0\.0/,
    /::1/,
    /file:\/\//i,
    /ftp:\/\//i,
    /gopher:\/\//i,
    /dict:\/\//i,
    /ldap:\/\//i,
    /jar:\/\//i,
    /netdoc:\/\//i,
    /http:\/\//i,
    /https:\/\//i,
    /@/,
    /\.\./,
    /\\/,
    /%/
  ];

  /**
   * Validates and sanitizes a client code parameter
   * @param clientCode The client code to validate
   * @returns The sanitized client code
   * @throws Error if validation fails
   */
  public static validateClientCode(clientCode: string): string {
    if (!clientCode) {
      throw new Error('Client code cannot be empty');
    }

    if (typeof clientCode !== 'string') {
      throw new TypeError('Client code must be a string');
    }

    // Trim whitespace
    const trimmed = clientCode.trim();

    // Check length
    if (trimmed.length === 0) {
      throw new Error('Client code cannot be empty after trimming');
    }

    if (trimmed.length > this.MAX_CLIENT_CODE_LENGTH) {
      throw new Error(`Client code cannot exceed ${this.MAX_CLIENT_CODE_LENGTH} characters`);
    }

    // Check for SSRF attack patterns
    for (const pattern of this.SSRF_PATTERNS) {
      if (pattern.test(trimmed)) {
        throw new Error('Client code contains invalid characters');
      }
    }

    // Validate against allowed pattern
    if (!this.CLIENT_CODE_PATTERN.test(trimmed)) {
      throw new Error('Client code can only contain alphanumeric characters, hyphens, and underscores');
    }

    return trimmed;
  }

  /**
   * Validates and sanitizes a currency code parameter
   * @param currencyCode The currency code to validate
   * @returns The sanitized currency code
   * @throws Error if validation fails
   */
  public static validateCurrencyCode(currencyCode: string): string {
    if (!currencyCode) {
      throw new Error('Currency code cannot be empty');
    }

    if (typeof currencyCode !== 'string') {
      throw new TypeError('Currency code must be a string');
    }

    // Trim and convert to uppercase
    const trimmed = currencyCode.trim().toUpperCase();

    // Check length
    if (trimmed.length !== this.MAX_CURRENCY_CODE_LENGTH) {
      throw new Error('Currency code must be exactly 3 characters');
    }

    // Check for SSRF attack patterns
    for (const pattern of this.SSRF_PATTERNS) {
      if (pattern.test(trimmed)) {
        throw new Error('Currency code contains invalid characters');
      }
    }

    // Validate against allowed pattern (3-letter ISO currency codes)
    if (!this.CURRENCY_CODE_PATTERN.test(trimmed)) {
      throw new Error('Currency code must be a valid 3-letter ISO currency code');
    }

    return trimmed;
  }

  /**
   * Validates and sanitizes an invoice code parameter
   * @param invoiceCode The invoice code to validate
   * @returns The sanitized invoice code
   * @throws Error if validation fails
   */
  public static validateInvoiceCode(invoiceCode: string): string {
    if (!invoiceCode) {
      throw new Error('Invoice code cannot be empty');
    }

    if (typeof invoiceCode !== 'string') {
      throw new TypeError('Invoice code must be a string');
    }

    // Trim whitespace
    const trimmed = invoiceCode.trim();

    // Check length
    if (trimmed.length === 0) {
      throw new Error('Invoice code cannot be empty after trimming');
    }

    if (trimmed.length > this.MAX_CLIENT_CODE_LENGTH) {
      throw new Error(`Invoice code cannot exceed ${this.MAX_CLIENT_CODE_LENGTH} characters`);
    }

    // Check for SSRF attack patterns
    for (const pattern of this.SSRF_PATTERNS) {
      if (pattern.test(trimmed)) {
        throw new Error('Invoice code contains invalid characters');
      }
    }

    // Validate against allowed pattern
    if (!this.CLIENT_CODE_PATTERN.test(trimmed)) {
      throw new Error('Invoice code can only contain alphanumeric characters, hyphens, and underscores');
    }

    return trimmed;
  }

  /**
   * Generic parameter validation for any string parameter
   * @param paramName The name of the parameter (for error messages)
   * @param paramValue The value to validate
   * @param maxLength Maximum allowed length
   * @returns The sanitized parameter value
   * @throws Error if validation fails
   */
  public static validateStringParameter(paramName: string, paramValue: string, maxLength: number = 100): string {
    if (!paramValue) {
      throw new Error(`${paramName} cannot be empty`);
    }

    if (typeof paramValue !== 'string') {
      throw new TypeError(`${paramName} must be a string`);
    }

    // Trim whitespace
    const trimmed = paramValue.trim();

    // Check length
    if (trimmed.length === 0) {
      throw new Error(`${paramName} cannot be empty after trimming`);
    }

    if (trimmed.length > maxLength) {
      throw new Error(`${paramName} cannot exceed ${maxLength} characters`);
    }

    // Check for SSRF attack patterns
    for (const pattern of this.SSRF_PATTERNS) {
      if (pattern.test(trimmed)) {
        throw new Error(`${paramName} contains invalid characters`);
      }
    }

    return trimmed;
  }
}
